/* eslint-disable @typescript-eslint/no-explicit-any */
import IconDots from '@assets/icons/dots.svg?react';
import IconLock from '@assets/icons/lock.svg?react';
import {
 Box,
 IconButton,
 ListItemIcon,
 Menu,
 MenuItem,
 Typography,
 useTheme,
} from '@mui/material';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useConfirmationDialog } from '../../../contexts/useConfirmationDialog';
import { useHistoryDialog } from '../../../contexts/useHistoryDialog';
import { useIVAZDialog } from '../../../contexts/useIVAZDialog';
import { usePage } from '../../../contexts/usePageContext';
import { useReportDialog } from '../../../contexts/useReportDialog';
import * as customersSlice from '../../../redux/customers/customersSlice';
import * as resultsSlice from '../../../redux/results/resultsSlice';
import LazySvg from '../LazySvg';
import { TableModalState } from './types';
import { useAuthContext } from '@/contexts/AuthContext';

const RowMenu = ({
 row,
 setCurrentId,
 setModalState,
}: {
 row: any;
 setCurrentId: any;
 setModalState: Dispatch<SetStateAction<TableModalState>>;
}) => {
 const { pageName } = usePage();
 const th = useTheme();
 const { t } = useTranslation();
 const dispatch = useDispatch();
 const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
 const { openDialog: openConfirmationDialog } = useConfirmationDialog();
 const { openDialog: openReportDialog } = useReportDialog();
 const { openDialog: openIVAZDialog } = useIVAZDialog();
 const { openDialog: openHistoryDialog } = useHistoryDialog();
 const { hasRole } = useAuthContext();

 void openIVAZDialog;
 void openHistoryDialog;

 const handleInfo = (id: any) => {
  setCurrentId(id);
  setAnchorEl(null);
 };

 const canEdit = useMemo(
  () =>
   hasRole(pageName, 'Get') &&
   (hasRole(pageName, 'Update') || hasRole(pageName, 'Patch')),
  [hasRole, pageName],
 );

 const canDelete = useMemo(
  () => hasRole(pageName, 'Delete'),
  [hasRole, pageName],
 );

 const menuItems = [
  {
   label: t('global.moreInfo'),
   icon: 'info',
   onClick: () => {
    handleInfo(row?.Id);
    setAnchorEl(null);
   },
  },
  ...(canEdit
   ? [
      {
       label: t('global.edit'),
       icon: 'edit',
       onClick: () => {
        setModalState({ id: row?.Id, action: 'update' });
        setAnchorEl(null);
       },
      },
     ]
   : []),
  ...(['results'].includes(pageName)
   ? [
      {
       label: t('global.duplicate'),
       icon: 'duplicate',
       onClick: () => {
        setModalState({ id: row?.Id, action: 'duplicate' });
        setAnchorEl(null);
       },
      },
     ]
   : []),
  ...(['results'].includes(pageName)
   ? [
      {
       label: t('global.generateReport'),
       icon: 'registration',
       onClick: () => {
        openReportDialog({
         id: row?.Id,
         onConfirm: () => {
          alert('?');
         },
        });
        setAnchorEl(null);
       },
      },
     ]
   : []),
  ...(['results'].includes(pageName)
   ? [
      {
       label: t('global.block'),
       icon: 'lock',
       onClick: () => {
        openConfirmationDialog({
         title: t('global.block'),
         message: t('global.blockMessage'),
         buttonText: t('global.block'),
         buttonProps: {
          color: 'primary',
          variant: 'contained',
          endIcon: <IconLock style={{ color: th.palette.kemek.white }} />,
         },
         onConfirm: () => {
          dispatch(resultsSlice.approveResultStart([row?.Id]));
         },
        });
        setAnchorEl(null);
       },
      },
     ]
   : []),
  ...(canDelete
   ? [
      {
       label: t('global.delete'),
       icon: 'delete',
       onClick: () => {
        openConfirmationDialog({
         title: t('global.delete'),
         message: t('global.deleteMessage'),
         onConfirm: () => {
          dispatch(
           resultsSlice.deleteItemStart({ table: pageName, id: row?.Id }),
          );
         },
        });
        setAnchorEl(null);
       },
      },
     ]
   : []),
  ...(['customers'].includes(pageName)
   ? [
      {
       label: row.IsEnabled ? t('global.deactivate') : t('global.activate'),
       icon: row.IsEnabled ? 'invisible' : 'visible',
       onClick: () => {
        dispatch(
         customersSlice.updateItemStart({
          table: pageName,
          id: row.Id,
          data: [{ op: 'replace', path: '/IsEnabled', value: !row.IsEnabled }],
         }),
        );
        setAnchorEl(null);
       },
      },
     ]
   : []),
 ];

 return (
  <Box>
   <IconButton
    aria-label="menu"
    color="primary"
    sx={{ width: '26px', height: '26px', p: 0 }}
    onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
     setAnchorEl(e.currentTarget)
    }
   >
    <IconDots />
   </IconButton>
   <Menu
    MenuListProps={{ disablePadding: true }}
    anchorEl={anchorEl}
    open={!!anchorEl}
    onClose={() => setAnchorEl(null)}
   >
    {menuItems.map((item, index) => (
     <MenuItem
      key={item.label}
      onClick={item.onClick}
      sx={{ color: item.icon === 'delete' ? 'red' : 'inherit' }}
      divider={index !== menuItems.length - 1}
     >
      <ListItemIcon>
       <LazySvg
        name={item.icon}
        styles={{
         '& path': {
          stroke:
           item.icon === 'delete'
            ? th.palette.kemek.red
            : th.palette.kemek.darkestGray,
         },
        }}
       />
      </ListItemIcon>
      <Typography variant="inherit">{item.label}</Typography>
     </MenuItem>
    ))}
   </Menu>
  </Box>
 );
};

export default RowMenu;
